/* ============================================================
   AutopilotLimitBanner — when autopilot pauses because the monthly
   post limit is spent, the ONLY signal used to be one bell row that
   sat unread for three days while publishing silently stopped
   (owner incident 2026-08-14..17). This banner puts the pause where
   the owner actually looks — the dashboard — with the two facts that
   matter: WHY it stopped and WHEN it resumes.

   Same contract as FreeLimitsBanner: owns its fetch, silent on any
   failure, renders nothing when there is nothing to say. Data source
   is the autopilot_plan notification the worker already writes; it
   carries metadata.resumeAt (usage period end), so the banner
   self-expires the moment publishing resumes — no cleanup path.
   Dismissal is per-notification (localStorage), so the same pause is
   dismissible once, but a NEW pause next period shows again.
   ============================================================ */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from './Icons.jsx';
import { api } from '../services/api/client.js';

const DISMISS_KEY = 'dathent.autopilotLimitBanner.dismissedId';

function readDismissedId() {
  try { return window.localStorage.getItem(DISMISS_KEY); } catch (_) { return null; }
}

function writeDismissedId(id) {
  try { window.localStorage.setItem(DISMISS_KEY, String(id)); } catch (_) { /* private mode */ }
}

export default function AutopilotLimitBanner() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [pause, setPause] = useState(null); // { id, resumeAt } when active
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.notifications.list({ limit: '30' });
        if (cancelled) return;
        const items = (res?.data ?? res)?.notifications || (res?.data ?? res)?.items || [];
        const now = Date.now();
        const hit = items.find((n) => {
          if (n?.type !== 'autopilot_plan') return false;
          const meta = n.metadata || {};
          const resumeMs = Date.parse(meta.resumeAt || '');
          return meta.limitReached === true && Number.isFinite(resumeMs) && resumeMs > now;
        });
        if (!hit) return;
        if (readDismissedId() === String(hit.id)) { setDismissed(true); }
        setPause({ id: hit.id, resumeAt: hit.metadata.resumeAt });
      } catch (_) { /* nudge, not a gate — stay silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!pause || dismissed) return null;

  let date;
  try {
    date = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long' }).format(new Date(pause.resumeAt));
  } catch (_) {
    date = String(pause.resumeAt).slice(0, 10);
  }

  return (
    <section className="card flb-banner" role="status">
      <span className="flb-ic">
        <Icon name="alert" size={14} />
      </span>
      <p className="flb-msg">
        {t('autopilotLimit.bannerMsg', {
          date,
          defaultValue: 'Autopilot is paused — this month’s post limit is used up. Publishing resumes {{date}}.',
        })}
      </p>
      <div className="flb-actions">
        <button type="button" className="btn btn-primary btn-sm flb-cta" onClick={() => navigate('/app/plans')}>
          {t('autopilotLimit.bannerCta', { defaultValue: 'View plans' })}
        </button>
        <button
          type="button"
          className="flb-x"
          aria-label={t('autopilotLimit.bannerDismiss', { defaultValue: 'Dismiss' })}
          onClick={() => { writeDismissedId(pause.id); setDismissed(true); }}
        >
          <Icon name="x" size={13} />
        </button>
      </div>
    </section>
  );
}
